import { Modal, ModalContent, ModalFooter, ModalHeader, Portal, RegularButton } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getHostname } from 'common/selectors/location';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { getRegionById } from 'features/stores/ducks/get-stores';
import { analyticsUtils, getDomain, getRegionFromUrl } from 'utils';
import { SHOP_URL } from 'common/constants';
import { cancelButton, modal, modalContent, modalFooter } from './style';
import { getDatePlusYear } from 'utils/date-time-helpers';
import { createCookies } from 'utils/cookies';
import { getCleanStageHostname } from 'utils/get-hostname';

type Props = {
  active: boolean;
  targetRegionId: string;
  targetStoreId: string;
  closeHandle: () => void;
};

export const RegionChangeDialog: React.FC<Props> = ({
  active,
  closeHandle,
  targetRegionId,
  targetStoreId,
}) => {
  const { t } = useTranslation();
  const hostname = useSelector(getHostname);
  const regionData = useSelector(getRegionById(targetRegionId));
  const storeData = useMemo(
    () => regionData?.stores?.find((store) => store.id === targetStoreId),
    [regionData?.stores, targetStoreId],
  );
  const regionFromUrl = getRegionFromUrl(regionData?.url);
  const regionUrlWithDot = useMemo(
    () => (targetRegionId === '34' ? '' : `${regionFromUrl}.`),
    [targetRegionId, regionFromUrl],
  );
  const { smMobile } = useBreakpoints() ?? {};
  const changeRegion = t('changeRegion');
  const shopAnotherRegion = t('shopAnotherRegion');
  const cancel = t('ui.cancel');
  const proceed = t('ui.proceed');

  const navigateToStore = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage',
      elementText: `${proceed} / ${storeData?.name}`, // Название кнопки / название магазина
      elementName: 'popup', // название блока
    });

    if (regionData) {
      const cookies = createCookies();
      const cookieDomain = getDomain(hostname);
      cookies.set('_userRegion', regionFromUrl, {
        path: '/',
        domain: `.${cookieDomain}`,
        expires: getDatePlusYear(),
      });
    }

    const isStaging = !!hostname.match(new RegExp('lm(ru|kz).tech'))?.length;
    const domain = isStaging ? getCleanStageHostname(hostname) : getDomain(hostname);
    window.open(`https://${regionUrlWithDot}${domain}${SHOP_URL}/${storeData?.url}`, '_blank');
    closeHandle();
  };

  const handleClosing = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage',
      elementText: `${cancel} / ${storeData?.name}`, // Название кнопки / название магазина
      elementName: 'popup', // название блока
    });
    closeHandle();
  };

  useEffect(() => {
    if (active) {
      analyticsUtils.sendEvent('GA:show', {
        elementType: 'block',
        elementBlock: 'shopPage',
        elementText: `${shopAnotherRegion} / ${storeData?.name}`, // Заголовок / название магазина / название подсказки
        elementName: 'popup', // название блока
      });
    }
  }, [active, storeData?.name, shopAnotherRegion]);

  const text = `${t('changeRegionDialog.text1')} ${storeData?.name}, ${t('changeRegionDialog.text2')}`;

  return (
    <>
      <Portal>
        <Modal
          className={modal}
          modalID="menu"
          onClose={handleClosing}
          size="s"
          variant="curtain"
          data-qa="change-region-modal"
          show={active}
        >
          <ModalHeader title={shopAnotherRegion} />
          <ModalContent className={modalContent}>{text}</ModalContent>
          {smMobile ? (
            <ModalFooter className={modalFooter}>
              <RegularButton
                onClick={handleClosing}
                size="l"
                wide
                variant="secondary"
                className={cancelButton}
                data-qa="b-close"
              >
                {cancel}
              </RegularButton>
              <RegularButton onClick={navigateToStore} size="l" wide variant="primary" data-qa="b-navigate">
                {changeRegion}
              </RegularButton>
            </ModalFooter>
          ) : (
            <ModalFooter className={modalFooter}>
              <RegularButton
                onClick={handleClosing}
                size="m"
                variant="pseudo"
                className={cancelButton}
                data-qa="b-close"
              >
                {cancel}
              </RegularButton>
              <RegularButton onClick={navigateToStore} size="m" variant="primary" data-qa="b-navigate">
                {changeRegion}
              </RegularButton>
            </ModalFooter>
          )}
        </Modal>
      </Portal>
    </>
  );
};
